import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import { FETCH_STATIONS_OVERVIEW } from 'actions/formActions';
import { 
    FaSearch, 
    FaTimes, 
    FaChargingStation, 
    FaCheckCircle, 
    FaClock, 
    FaFileAlt, 
    FaTrash,
    FaUserTie,
    FaUserCheck,
    FaShieldAlt,
    FaFileInvoice,
    FaClipboardList,
    FaUserClock,
    FaFolderOpen,
    FaTools
} from 'react-icons/fa';
import { deleteStation } from 'api/formApi';
import { toast } from 'react-toastify';
import { cookie } from 'utils/cookies';
import Pagination from 'components/general/Pagination';

const ShimmerLoader = () => {
    return (
        <div className="glass-panel shadow-sm p-4" style={{ overflow: 'hidden', background: '#fff', borderRadius: '16px' }}>
            <style dangerouslySetInnerHTML={{__html: `
                @keyframes shimmer {
                    0% {
                        background-position: -468px 0;
                    }
                    100% {
                        background-position: 468px 0;
                    }
                }
                .shimmer-element {
                    animation: shimmer 1.5s linear infinite;
                    background: linear-gradient(to right, #f1f5f9 8%, #e2e8f0 18%, #f1f5f9 33%);
                    background-size: 800px 104px;
                    position: relative;
                }
            `}} />
            <div className="table-responsive">
                <table className="premium-table" style={{ pointerEvents: 'none', width: '100%' }}>
                    <thead>
                        <tr>
                            <th><div className="shimmer-element" style={{ height: '16px', width: '120px', borderRadius: '4px' }}></div></th>
                            <th><div className="shimmer-element" style={{ height: '16px', width: '150px', borderRadius: '4px' }}></div></th>
                            <th><div className="shimmer-element" style={{ height: '16px', width: '100px', borderRadius: '4px' }}></div></th>
                            <th><div className="shimmer-element" style={{ height: '16px', width: '80px', borderRadius: '4px' }}></div></th>
                            <th style={{ textAlign: 'center' }}><div className="shimmer-element m-auto" style={{ height: '16px', width: '80px', borderRadius: '4px' }}></div></th>
                        </tr>
                    </thead>
                    <tbody>
                        {[1, 2, 3, 4, 5].map((i) => (
                            <tr key={i}>
                                <td className="py-3">
                                    <div className="d-flex align-items-center gap-2">
                                        <div className="shimmer-element" style={{ width: '36px', height: '36px', borderRadius: '10px', flexShrink: 0 }}></div>
                                        <div className="shimmer-element" style={{ height: '16px', width: '120px', borderRadius: '4px' }}></div>
                                    </div>
                                </td>
                                <td className="py-3">
                                    <div className="shimmer-element" style={{ height: '14px', width: '180px', borderRadius: '4px' }}></div>
                                </td>
                                <td className="py-3">
                                    <div className="shimmer-element" style={{ height: '14px', width: '100px', borderRadius: '4px' }}></div>
                                </td>
                                <td className="py-3">
                                    <div className="shimmer-element" style={{ height: '24px', width: '90px', borderRadius: '20px' }}></div>
                                </td>
                                <td className="py-3" style={{ textAlign: 'center' }}>
                                    <div className="d-flex gap-2 justify-content-center">
                                        <div className="shimmer-element" style={{ width: '32px', height: '32px', borderRadius: '8px' }}></div>
                                        <div className="shimmer-element" style={{ width: '32px', height: '32px', borderRadius: '8px' }}></div>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const FormList = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { 
        loading, 
        stationsOverview, 
        stationsPagination, 
        stationsTabCounts, 
        stationsStats, 
        stationsUniqueUsers 
    } = useSelector((state) => state.checklist);
    const authUser = useSelector((state) => state.auth?.user?.user || state.auth?.user);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [userFilter, setUserFilter] = useState('');
    const [pendingRoleFilter, setPendingRoleFilter] = useState('');
    const [activeSectionTab, setActiveSectionTab] = useState(location?.state?.fromTab || 'all'); // 'all' | 'pending_from_you' | 'my_items' | 'other_projects'
    const [isSearchExpanded, setIsSearchExpanded] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const limit = 10;
    
    const userRole = (authUser?.role || cookie.get('user-role') || '').toLowerCase();
    const isTwoSectionRole = ['quality', 'operations', 'operation', 'maintenance', 'manager', 'engineer', 'contractor', 'admin'].includes(userRole);
    const isAdmin = userRole === 'admin';

    // Debounce search input to avoid redundant requests on every keystroke
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search);
            setCurrentPage(1);
        }, 200);
        return () => clearTimeout(timer);
    }, [search]);

    // Fetch server-side aggregated data on filter/tab/page change
    useEffect(() => {
        dispatch({
            type: FETCH_STATIONS_OVERVIEW,
            payload: {
                tab: activeSectionTab,
                search: debouncedSearch,
                userFilter,
                filter: pendingRoleFilter,
                page: currentPage,
                limit
            }
        });
    }, [dispatch, activeSectionTab, debouncedSearch, userFilter, pendingRoleFilter, currentPage]);

    const handleDeleteStation = async (stationId) => {
        if (window.confirm(`Are you sure you want to delete station "${stationId}" and all its associated forms? This action cannot be undone.`)) {
            try {
                const response = await deleteStation(stationId);
                if (response.status === 200) {
                    toast.success('Station and associated forms deleted successfully');
                    dispatch({
                        type: FETCH_STATIONS_OVERVIEW,
                        payload: {
                            tab: activeSectionTab,
                            search,
                            userFilter,
                            filter: pendingRoleFilter,
                            page: currentPage,
                            limit
                        }
                    });
                } else {
                    toast.error(response.message || 'Failed to delete station');
                }
            } catch (error) {
                toast.error(error.message || 'An error occurred while deleting the station');
            }
        }
    };

    const paginatedList = Array.isArray(stationsOverview) ? stationsOverview : [];
    const total = stationsPagination?.totalResults || 0;
    const totalPages = stationsPagination?.totalPages || 1;
    const uniqueUsers = stationsUniqueUsers || [];

    const handlePageClick = ({ selected }) => {
        setCurrentPage(selected + 1);
    };

    const totalStations = stationsStats?.totalStations || 0;
    const totalApproved = stationsStats?.totalApproved || 0;
    const totalDraft = stationsStats?.totalDraft || 0;
    const totalPending = stationsStats?.totalPending || 0;
    const totalQualityPending = stationsStats?.totalQualityPending || 0;
    const totalManagerPending = stationsStats?.totalManagerPending || 0;
    const totalContractorPending = stationsStats?.totalContractorPending || 0;
    const totalMyApprovedForms = stationsStats?.totalMyApprovedForms || 0;

    const allStatCards = [
        { label: 'Total Stations', value: totalStations, icon: <FaChargingStation size={14} />, iconColor: '#475569', iconBg: '#f1f5f9', filterKey: 'all' },
        { label: 'Approved', value: totalApproved, icon: <FaCheckCircle size={14} />, iconColor: '#16a34a', iconBg: '#dcfce7', filterKey: 'approved' },
        { label: 'Total Pending', value: totalPending, icon: <FaClock size={14} />, iconColor: '#ea580c', iconBg: '#ffedd5', filterKey: 'pending' },
        { label: 'Draft', value: totalDraft, icon: <FaFileAlt size={14} />, iconColor: '#2563eb', iconBg: '#dbeafe', filterKey: 'draft' },
        { label: 'Approved by You', value: totalMyApprovedForms, icon: <FaCheckCircle size={14} />, iconColor: '#059669', iconBg: '#d1fae5', filterKey: 'my_items' },
        { label: 'Pending Quality', value: totalQualityPending, icon: <FaShieldAlt size={14} />, iconColor: '#e11d48', iconBg: '#ffe4e6', filterKey: 'quality' },
        { label: 'Pending Manager', value: totalManagerPending, icon: <FaUserCheck size={14} />, iconColor: '#0284c7', iconBg: '#e0f2fe', filterKey: 'manager' },
        { label: 'Pending Contractor', value: totalContractorPending, icon: <FaUserTie size={14} />, iconColor: '#9333ea', iconBg: '#f3e8ff', filterKey: 'contractor' },
    ];

    return (
        <div className="page-body checklist-container">
            <div className="container-xl">

                {/* Stat Cards Grid */}
                <div className="row g-2 mb-3">
                    {allStatCards.map((card) => {
                        const isSelected = card.filterKey === 'all' 
                            ? !pendingRoleFilter 
                            : pendingRoleFilter === card.filterKey;
                        return (
                            <div className="col-6 col-sm-4 col-md-3 col-xl" key={card.label}>
                                <div 
                                    onClick={() => {
                                        setCurrentPage(1);
                                        if (card.filterKey === 'all') {
                                            setPendingRoleFilter('');
                                        } else {
                                            setPendingRoleFilter(prev => prev === card.filterKey ? '' : card.filterKey);
                                        }
                                    }}
                                    style={{
                                        background: isSelected ? card.iconBg : '#ffffff',
                                        border: isSelected ? `2px solid ${card.iconColor}` : '1px solid #e2e8f0',
                                        borderRadius: '12px',
                                        padding: '10px 14px',
                                        boxShadow: isSelected 
                                            ? `0 2px 8px 0 ${card.iconColor}25` 
                                            : '0 1px 2px 0 rgba(0,0,0,0.04)',
                                        transition: 'all 0.15s ease',
                                        cursor: 'pointer',
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center'
                                    }}
                                    title={isSelected ? (card.filterKey === 'all' ? 'All stations currently shown' : 'Click to clear filter') : `Filter by ${card.label}`}
                                >
                                    <div className="d-flex align-items-center justify-content-between mb-1">
                                        <span style={{ 
                                            color: isSelected ? card.iconColor : '#64748b', 
                                            fontSize: '11px', 
                                            fontWeight: 700, 
                                            textTransform: 'uppercase', 
                                            letterSpacing: '0.03em',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis'
                                        }}>
                                            {card.label}
                                        </span>
                                        <span style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: '26px',
                                            height: '26px',
                                            borderRadius: '8px',
                                            background: isSelected ? '#ffffff' : card.iconBg,
                                            color: card.iconColor,
                                            flexShrink: 0
                                        }}>{card.icon}</span>
                                    </div>
                                    <div style={{ fontSize: '20px', fontWeight: 800, lineHeight: 1.1, color: '#0f172a' }}>
                                        {card.value}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Section Tabs */}
                {isTwoSectionRole && (
                    <div className="d-flex align-items-center gap-2 mb-3 p-1" style={{ background: '#f1f5f9', borderRadius: '14px', width: 'fit-content', flexWrap: 'wrap' }}>
                        <button
                            id="tab-all-projects"
                            onClick={() => {
                                setCurrentPage(1);
                                setActiveSectionTab('all');
                            }}
                            style={{
                                border: 'none',
                                background: activeSectionTab === 'all' ? '#ffffff' : 'transparent',
                                color: activeSectionTab === 'all' ? '#1e293b' : '#64748b',
                                fontWeight: activeSectionTab === 'all' ? 700 : 600,
                                fontSize: '13px',
                                padding: '8px 18px',
                                borderRadius: '10px',
                                boxShadow: activeSectionTab === 'all' ? '0 2px 6px rgba(0,0,0,0.06)' : 'none',
                                transition: 'all 0.2s ease',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}
                        >
                            <FaTools style={{ color: activeSectionTab === 'all' ? '#475569' : '#64748b' }} />
                            <span>All Projects</span>
                            <span style={{
                                background: activeSectionTab === 'all' ? '#f1f5f9' : '#e2e8f0',
                                color: '#475569',
                                fontSize: '11px',
                                fontWeight: 700,
                                padding: '2px 8px',
                                borderRadius: '12px'
                            }}>
                                {stationsTabCounts?.all || 0}
                            </span>
                        </button>

                        <button
                            id="tab-pending-from-you"
                            onClick={() => {
                                setCurrentPage(1);
                                setActiveSectionTab('pending_from_you');
                            }}
                            style={{
                                border: 'none',
                                background: activeSectionTab === 'pending_from_you' ? '#ffffff' : 'transparent',
                                color: activeSectionTab === 'pending_from_you' ? '#1e293b' : '#64748b',
                                fontWeight: activeSectionTab === 'pending_from_you' ? 700 : 600,
                                fontSize: '13px',
                                padding: '8px 18px',
                                borderRadius: '10px',
                                boxShadow: activeSectionTab === 'pending_from_you' ? '0 2px 6px rgba(0,0,0,0.06)' : 'none',
                                transition: 'all 0.2s ease',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}
                        >
                            <FaUserClock style={{ color: activeSectionTab === 'pending_from_you' ? '#2563eb' : '#64748b' }} />
                            <span>Pending from You</span>
                            <span style={{
                                background: activeSectionTab === 'pending_from_you' ? '#dbeafe' : '#e2e8f0',
                                color: activeSectionTab === 'pending_from_you' ? '#1d4ed8' : '#475569',
                                fontSize: '11px',
                                fontWeight: 700,
                                padding: '2px 8px',
                                borderRadius: '12px'
                            }}>
                                {stationsTabCounts?.pendingFromYou || 0}
                            </span>
                        </button>

                        <button
                            id="tab-my-items"
                            onClick={() => {
                                setCurrentPage(1);
                                setActiveSectionTab('my_items');
                            }}
                            style={{
                                border: 'none',
                                background: activeSectionTab === 'my_items' ? '#ffffff' : 'transparent',
                                color: activeSectionTab === 'my_items' ? '#1e293b' : '#64748b',
                                fontWeight: activeSectionTab === 'my_items' ? 700 : 600,
                                fontSize: '13px',
                                padding: '8px 18px',
                                borderRadius: '10px',
                                boxShadow: activeSectionTab === 'my_items' ? '0 2px 6px rgba(0,0,0,0.06)' : 'none',
                                transition: 'all 0.2s ease',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}
                        >
                            <FaCheckCircle style={{ color: activeSectionTab === 'my_items' ? '#16a34a' : '#64748b' }} />
                            <span>My Items</span>
                            <span style={{
                                background: activeSectionTab === 'my_items' ? '#dcfce7' : '#e2e8f0',
                                color: activeSectionTab === 'my_items' ? '#15803d' : '#475569',
                                fontSize: '11px',
                                fontWeight: 700,
                                padding: '2px 8px',
                                borderRadius: '12px'
                            }}>
                                {stationsTabCounts?.myItems || 0}
                            </span>
                        </button>

                        <button
                            id="tab-other-projects"
                            onClick={() => {
                                setCurrentPage(1);
                                setActiveSectionTab('other_projects');
                            }}
                            style={{
                                border: 'none',
                                background: activeSectionTab === 'other_projects' ? '#ffffff' : 'transparent',
                                color: activeSectionTab === 'other_projects' ? '#1e293b' : '#64748b',
                                fontWeight: activeSectionTab === 'other_projects' ? 700 : 600,
                                fontSize: '13px',
                                padding: '8px 18px',
                                borderRadius: '10px',
                                boxShadow: activeSectionTab === 'other_projects' ? '0 2px 6px rgba(0,0,0,0.06)' : 'none',
                                transition: 'all 0.2s ease',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}
                        >
                            <FaFolderOpen style={{ color: activeSectionTab === 'other_projects' ? '#ea580c' : '#64748b' }} />
                            <span>Other Projects</span>
                            <span style={{
                                background: activeSectionTab === 'other_projects' ? '#ffedd5' : '#e2e8f0',
                                color: activeSectionTab === 'other_projects' ? '#c2410c' : '#475569',
                                fontSize: '11px',
                                fontWeight: 700,
                                padding: '2px 8px',
                                borderRadius: '12px'
                            }}>
                                {stationsTabCounts?.otherProjects || 0}
                            </span>
                        </button>
                    </div>
                )}

                {/* Search & Filter Section */}
                <div className="premium-filter-section mb-4">
                    <div className={`filter-group search-wrapper ${search || isSearchExpanded ? 'expanded' : ''}`}>
                        <label htmlFor="station-search">Search Stations</label>
                        <div className="position-relative">
                            <Form.Control
                                id="station-search"
                                type="text"
                                placeholder="Search by Station, Filer or Project..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                onFocus={() => setIsSearchExpanded(true)}
                                onBlur={() => !search && setIsSearchExpanded(false)}
                            />
                            <FaSearch className="search-icon" />
                        </div>
                    </div>
                    <div className="filter-group select-wrapper">
                        <label htmlFor="user-filter">Filter by Filer</label>
                        <Form.Select
                            id="user-filter"
                            value={userFilter}
                            onChange={(e) => setUserFilter(e.target.value)}
                            style={{ borderRadius: '12px', border: '1px solid #e2e8f0', height: '42px', paddingLeft: '15px' }}
                        >
                            <option value="">All Filers</option>
                            {uniqueUsers.map(u => (
                                <option key={u} value={u}>{u}</option>
                            ))}
                        </Form.Select>
                    </div>
                    { (search || userFilter || pendingRoleFilter) && (
                        <div className="filter-group flex-grow-0">
                            <span className="filter-label-spacer" aria-hidden="true">&nbsp;</span>
                            <button id="clear-search-btn" className="btn-clear" onClick={() => { setSearch(''); setUserFilter(''); setPendingRoleFilter(''); }} title="Clear Filters">
                                <FaTimes /> Clear
                            </button>
                        </div>
                    )}
                </div>

                {/* Table */}
                {loading ? (
                    <ShimmerLoader />
                ) : (
                    <div className="table-responsive">
                        <table className="premium-table">
                            <thead>
                                <tr>
                                    <th>Station</th>
                                    <th>Project</th>
                                    <th>Created By</th>
                                    <th>Status</th>
                                    <th style={{ textAlign: 'center' }}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedList.map((form) => (
                                    <tr 
                                        key={form.stationId}
                                        onClick={() => navigate(`/formList/${form.stationId}`, { state: { fromTab: activeSectionTab } })}
                                        style={{ 
                                            cursor: 'pointer',
                                            ...(form.isRecent ? { borderLeft: '4px solid #2563eb' } : {})
                                        }}
                                    >
                                        <td>
                                            <div className="d-flex align-items-center gap-2">
                                                <div style={{
                                                    width: '36px', height: '36px', borderRadius: '10px',
                                                    background: '#f1f5f9',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    flexShrink: 0,
                                                }}>
                                                    <FaChargingStation color="#475569" size={16} />
                                                </div>
                                                <div>
                                                    <span
                                                        className="fw-bold"
                                                        role="button"
                                                        tabIndex="0"
                                                        style={{ color: '#1e293b', fontSize: '14px' }}
                                                    >
                                                        {form.stationId}
                                                    </span>
                                                    {form.hasMyItems && activeSectionTab === 'my_items' && (
                                                        <span 
                                                            className="badge ms-2"
                                                            style={{ background: '#dcfce7', color: '#15803d', fontSize: '11px', fontWeight: 600, padding: '2px 7px', borderRadius: '6px' }}
                                                            title="Forms approved/done by you in this station"
                                                        >
                                                            {form.myDoneCount} Approved
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td><span className="text-muted" style={{ fontSize: '13px' }}>{form.project_name}</span></td>
                                        <td><span className="text-muted" style={{ fontSize: '13px' }}>{form.userName}</span></td>
                                        <td>
                                            <span className={`status-pill status-${form.status}`} style={{ width: 'fit-content' }}>
                                                {form.status === 'completed' ? 'Completed' : 'In Progress'}
                                            </span>
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            <div className="action-btn-group" style={{ justifyContent: 'center', gap: '6px' }}>
                                                <button
                                                    className="btn-action-badge btn-sat"
                                                    title="Redirect to current SAT forms"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigate(`/formList/${form.stationId}`, { state: { fromTab: activeSectionTab } });
                                                    }}
                                                >
                                                    <FaFileAlt style={{ fontSize: '11px' }} /> SAT
                                                </button>
                                                <button
                                                    className="btn-action-badge btn-jms"
                                                    title="JMS Forms"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        toast.info(`JMS forms for ${form.stationId}`);
                                                    }}
                                                >
                                                    <FaClipboardList style={{ fontSize: '11px' }} /> JMS
                                                </button>
                                                <button
                                                    className="btn-action-badge btn-invoice"
                                                    title="Invoice"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        toast.info(`Invoice for ${form.stationId}`);
                                                    }}
                                                >
                                                    <FaFileInvoice style={{ fontSize: '11px' }} /> Invoice
                                                </button>
                                                {(isAdmin || userRole === 'manager') && (
                                                    <button
                                                        className="btn-premium btn-danger-soft"
                                                        title="Delete Station"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDeleteStation(form.stationId);
                                                        }}
                                                        style={{ background: '#fee2e2', color: '#ef4444', height: '31px', width: '31px', borderRadius: '8px' }}
                                                    >
                                                        <FaTrash size={12} />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {paginatedList.length === 0 && (
                                    <tr>
                                        <td colSpan="9" className="text-center py-5">
                                            <div className="d-flex flex-column align-items-center">
                                                <FaChargingStation size={48} className="text-muted mb-3" style={{ opacity: 0.3 }} />
                                                <p className="text-muted fw-bold mb-1">No stations found</p>
                                                <p className="text-muted small">
                                                    {activeSectionTab === 'pending_from_you' 
                                                        ? 'No projects currently pending your action.' 
                                                        : activeSectionTab === 'my_items'
                                                        ? 'No projects or forms approved by you yet.'
                                                        : 'Try adjusting your search criteria or filters.'}
                                                </p>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                {!loading && total > 0 && (
                    <div className="mt-4 d-flex justify-content-end">
                        <Pagination
                            total={total}
                            handlePageClick={handlePageClick}
                            currentPage={currentPage}
                            limit={limit}
                            totalPages={totalPages}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default FormList;